<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import LeadStageTransitionsAPI from 'dashboard/api/leadStageTransitions';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';

const props = defineProps({
  contactId: { type: [String, Number], required: true },
  conversationId: { type: [String, Number], default: '' },
});

const STAGE_OPTIONS = [
  { value: 'sem_visita', label: 'Sem visita', icon: 'i-lucide-circle' },
  {
    value: 'uma_ou_duas_visitas',
    label: '1 ou 2 visitas',
    icon: 'i-lucide-calendar-check',
  },
  {
    value: 'tres_ou_mais_visitas',
    label: '3 ou mais visitas',
    icon: 'i-lucide-calendar-days',
  },
  {
    value: 'proposta_em_andamento',
    label: 'Proposta em andamento',
    icon: 'i-lucide-file-text',
  },
  {
    value: 'negocio_fechado',
    label: 'Negocio fechado',
    icon: 'i-lucide-badge-check',
  },
];

const transitions = ref([]);
const meta = ref({ count: 0 });
const isLoading = ref(false);
const isSaving = ref(false);
const form = ref({ stage: '', reason: '' });

const stageLabel = stage =>
  STAGE_OPTIONS.find(option => option.value === stage)?.label || stage;
const stageIcon = stage =>
  STAGE_OPTIONS.find(option => option.value === stage)?.icon ||
  'i-lucide-activity';
const localizedDate = value => {
  if (!value) return 'Sem data';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Data invalida';
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(parsed);
};
const currentTransition = computed(
  () => transitions.value[transitions.value.length - 1] || null
);
const currentStage = computed(() => currentTransition.value?.to_stage || '');
const hasTransitions = computed(() => transitions.value.length > 0);
const currentStageLabel = computed(
  () =>
    currentTransition.value?.stage_label ||
    stageLabel(currentStage.value) ||
    'Sem historico'
);
const transitionCountLabel = computed(() => {
  const count = meta.value.count ?? transitions.value.length;
  return count === 1 ? '1 transicao' : `${count} transicoes`;
});

const resetFormStage = () => {
  form.value.stage = currentStage.value || STAGE_OPTIONS[0].value;
};
const loadTransitions = async () => {
  if (!props.contactId) return;

  isLoading.value = true;
  try {
    const { data } = await LeadStageTransitionsAPI.list({
      contact_id: props.contactId,
      per_page: 50,
    });
    transitions.value = data.data || [];
    meta.value = data.meta || { count: transitions.value.length };
    resetFormStage();
  } catch {
    transitions.value = [];
    useAlert('Nao foi possivel carregar a timeline de estagios.');
  } finally {
    isLoading.value = false;
  }
};
const saveManualCorrection = async () => {
  const reason = form.value.reason.trim();
  if (!form.value.stage || !reason) {
    useAlert('Informe o estagio e o motivo da correcao.');
    return;
  }

  isSaving.value = true;
  try {
    await LeadStageTransitionsAPI.create({
      contact_id: props.contactId,
      conversation_id: props.conversationId || undefined,
      source_type: props.conversationId ? 'Conversation' : 'Contact',
      source_id: props.conversationId || props.contactId,
      stage: form.value.stage,
      reason,
      metadata: { ui_surface: 'lead_stage_timeline_panel' },
    });
    form.value.reason = '';
    await loadTransitions();
    useAlert('Estagio do cliente atualizado.');
  } catch (error) {
    useAlert(
      error.response?.data?.errors?.join(', ') ||
        'Nao foi possivel registrar a correcao de estagio.'
    );
  } finally {
    isSaving.value = false;
  }
};

onMounted(loadTransitions);
watch(
  () => [props.contactId, props.conversationId],
  () => loadTransitions()
);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <LaisCard title="Timeline de estagios" prefix-icon="i-lucide-git-branch">
    <div class="space-y-5">
      <section
        class="flex flex-col gap-3 rounded-lais border border-lais-border-soft bg-[#FBFCFD] p-4 sm:flex-row sm:items-center sm:justify-between"
      >
        <div class="min-w-0">
          <p class="text-[12px] font-semibold uppercase text-lais-text-muted">
            Estagio atual
          </p>
          <p class="mt-1 truncate text-[17px] font-semibold text-lais-text">
            {{ currentStageLabel }}
          </p>
          <p class="mt-1 text-[12px] text-lais-text-muted">
            {{ transitionCountLabel }}
          </p>
        </div>
        <span
          class="grid size-11 shrink-0 place-items-center rounded-full bg-[#EAF6F1] text-[#176B55]"
          aria-hidden="true"
        >
          <span class="size-5" :class="stageIcon(currentStage)" />
        </span>
      </section>

      <section
        class="grid gap-3 rounded-lais border border-lais-border-soft bg-white p-4 lg:grid-cols-[minmax(160px,220px)_1fr_auto]"
      >
        <label
          class="flex min-w-0 flex-col gap-1 text-[12px] font-semibold text-lais-text-muted"
        >
          Estagio
          <select
            v-model="form.stage"
            class="min-h-10 rounded-lais border border-lais-border-soft bg-white px-3 text-[14px] text-lais-text"
          >
            <option
              v-for="option in STAGE_OPTIONS"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </label>
        <label
          class="flex min-w-0 flex-col gap-1 text-[12px] font-semibold text-lais-text-muted"
        >
          Motivo
          <input
            v-model.trim="form.reason"
            type="text"
            class="min-h-10 rounded-lais border border-lais-border-soft bg-white px-3 text-[14px] text-lais-text"
            placeholder="Motivo obrigatorio"
          />
        </label>
        <div class="flex items-end">
          <LaisPrimaryButton
            icon="i-lucide-save"
            variant="ghost"
            :is-loading="isSaving"
            @click="saveManualCorrection"
          >
            Registrar
          </LaisPrimaryButton>
        </div>
      </section>

      <div
        v-if="isLoading && !hasTransitions"
        class="rounded-lais border border-lais-border-soft bg-white p-5 text-[14px] text-lais-text-muted"
      >
        Carregando timeline...
      </div>
      <ol v-else-if="hasTransitions" class="space-y-3">
        <li
          v-for="transition in transitions"
          :key="transition.id"
          class="grid grid-cols-[auto_1fr] gap-3 rounded-lais border border-lais-border-soft bg-white p-3"
        >
          <span
            class="mt-0.5 grid size-8 place-items-center rounded-full bg-[#E8F0FE] text-[#2457A6]"
            aria-hidden="true"
          >
            <span class="size-4" :class="stageIcon(transition.to_stage)" />
          </span>
          <div class="min-w-0">
            <div class="flex flex-wrap items-start justify-between gap-2">
              <p class="text-[13px] font-semibold text-lais-text">
                {{ transition.stage_label || stageLabel(transition.to_stage) }}
              </p>
              <time class="text-[11px] text-lais-text-muted">
                {{ localizedDate(transition.occurred_at) }}
              </time>
            </div>
            <p
              v-if="transition.reason"
              class="mt-1 text-[12px] leading-relaxed text-lais-text-body"
            >
              {{ transition.reason }}
            </p>
            <p class="mt-1 text-[11px] text-lais-text-muted">
              {{
                transition.from_stage
                  ? stageLabel(transition.from_stage)
                  : 'Inicio'
              }}
              -> {{ stageLabel(transition.to_stage) }}
            </p>
          </div>
        </li>
      </ol>
      <div
        v-else
        class="rounded-lais border border-dashed border-lais-border-soft bg-[#FBFCFD] p-5 text-center"
      >
        <span
          class="i-lucide-git-branch mx-auto size-8 text-lais-text-muted"
          aria-hidden="true"
        />
        <p class="mt-3 text-[14px] font-semibold text-lais-text">
          Sem transicoes registradas.
        </p>
      </div>
    </div>
  </LaisCard>
</template>
